import { z } from 'zod'

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Author validation schema
const authorSchema = z
  .object({
    first_name: z
      .string()
      .min(1, 'First name is required')
      .max(100, 'First name must be less than 100 characters'),
    last_name: z
      .string()
      .min(1, 'Last name is required')
      .max(100, 'Last name must be less than 100 characters'),
    corresponding_email: z.string().optional(),
    is_corresponding: z.boolean(),
    is_presenting: z.boolean(),
    position: z.string().optional(),
    abstract_author_institutions_attributes: z
      .array(
        z.object({
          abstract_institution_id: z.string().min(1, 'Institution is required'),
        })
      )
      .min(1, 'At least one institution is required'),
  })
  .superRefine((author, ctx) => {
    // If author is marked as corresponding, email is required and must be valid
    if (author.is_corresponding) {
      if (
        !author.corresponding_email ||
        author.corresponding_email.trim() === ''
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Email is required for corresponding author',
          path: ['corresponding_email'],
        })
      } else if (!emailRegex.test(author.corresponding_email.trim())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please enter a valid email address',
          path: ['corresponding_email'],
        })
      }
    }
  })

// Institution validation schema
const institutionSchema = z
  .string()
  .min(1, 'Institution name is required')
  .max(200, 'Institution name must be less than 200 characters')
  .regex(
    /^(?!.*[\u4E00-\u9FFF]).*$/s,
    'Institution can only contain alphanumerical characters'
  )

// IUPAC Poster Award validation schema
const posterAwardSchema = z
  .object({
    paParticipation: z.boolean(),
    paAge: z.coerce.number().optional(),
    paProofOfIdentity: z.any().optional(),
  })
  .superRefine((data, ctx) => {
    // If paParticipation is true, require paAge and paProofOfIdentity
    if (data.paParticipation) {

      // Validate paAge
      if (!data.paAge) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Age is required for IUPAC Poster Award participation',
          path: ['paAge'],
        })
      } else {
        // Validate minimum age
        const age = parseInt(data.paAge, 10)
        if (isNaN(age) || age < 18) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'You must be at least 18 years old for IUPAC Poster Award participation',
            path: ['paAge'],
          })
        }
      }

      // Validate paProofOfIdentity
      if (!data.paProofOfIdentity) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Proof of identity is required for IUPAC Poster Award participation',
          path: ['paProofOfIdentity'],
        })
      } else {
        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']
        if (!allowedTypes.includes(data.paProofOfIdentity.type)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Proof of identity must be a JPEG, JPG, PNG, or PDF file',
            path: ['paProofOfIdentity'],
          })
        }

        // Validate file size (max 5 MB)
        const maxSizeInBytes = 5 * 1024 * 1024 // 5 MB
        if (data.paProofOfIdentity.size > maxSizeInBytes) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Proof of identity file size must not exceed 5 MB',
            path: ['paProofOfIdentity'],
          })
        }
      }
    }
  })

// Step 1 validation schema
export const step1Schema = z.object({
  mode: z.string().min(1, 'Presentation mode is required'),
  category: z.string().min(1, 'Abstract category is required'),
  topic: z.string().min(1, 'Abstract topic is required'),

  // IUPAC Poster Award
  posterAward: posterAwardSchema,
})

// Step 2 validation schema
export const step2Schema = z.object({
  institutions: z
    .array(institutionSchema)
    .min(1, 'At least one institution is required'),
  authors: z
    .array(authorSchema)
    .min(1, 'At least one author is required')
    .refine(
      (authors) => authors.some((a) => a.is_presenting),
      { message: 'There must be at least one presenting author' }
    )
    .refine(
      (authors) => {
        const correspondingAuthors = authors.filter((a) => a.is_corresponding)
        return (
          correspondingAuthors.length >= 1 && correspondingAuthors.length <= 2
        )
      },
      { message: 'There must be between 1 and 2 corresponding authors' }
    )
    .refine(
      (authors) => {
        // All authors must have positions assigned
        const allAuthorsHavePositions = authors.every(
          (author) => author.position && author.position.trim() !== ''
        )

        if (!allAuthorsHavePositions) {
          return false
        }

        // For multiple authors, validate position uniqueness and main author
        if (authors.length > 1) {
          const positions = authors.map((a) => a.position).filter((p) => p)
          const mainAuthorCount = positions.filter((p) => p === '1').length
          const hasDuplicates = new Set(positions).size !== positions.length
          return mainAuthorCount === 1 && !hasDuplicates
        }

        return true
      },
      {
        message: 'Assign unique positions (main/co-authors) to all authors.',
      }
    ),
})

// Step 3 validation schema
export const step3Schema = z.object({
  title: z
    .string()
    .min(1, 'Title is required')
    .regex(
      /^(?!.*[\u4E00-\u9FFF]).*$/s,
      'Title can only contain alphanumerical characters'
    )
    .refine(
      (title) => {
        // Strip HTML tags
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = title
        const textContent = tempDiv.textContent || tempDiv.innerText || ''
        return textContent.length <= 500
      },
      { message: 'Title must not exceed 500 characters' }
    ),
  content: z
    .string()
    .min(1, 'Content is required')
    .regex(
      /^(?!.*[\u4E00-\u9FFF]).*$/s,
      'Content can only contain alphanumerical characters'
    )
    .refine(
      (content) => {
        // Strip HTML tags the same way as the UI counter
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = content
        const textContent = tempDiv.textContent || tempDiv.innerText || ''
        const wordCount = textContent
          .trim()
          .split(/\s+/)
          .filter((word) => word.length > 0).length
        return wordCount >= 1 && wordCount <= 300
      },
      { message: 'Content must be between 1 and 300 words' }
    ),
  abstractImages: z
    .array(
      z.object({
        file: z
          .instanceof(File)
          .refine((file) => file.size <= 3.5 * 1024 * 1024, {
            message: 'Each image must not exceed 3.5 MB',
          }),
        preview: z.string(),
        // title: z.string().min(1, 'Image title is required'),
        position: z.number(),
      })
    )
    // .min(1, 'At least one image is required')
    .max(2, 'Maximum 2 images allowed'),
  imagePosition: z.enum(['side_to_side', 'up_and_down'], {
    errorMap: () => ({ message: 'Please select an image display format' }),
  }),
  keywords: z
    .array(z.string())
    .min(1, 'At least one keyword is required')
    .max(5, 'Maximum 5 keywords allowed'),
  references: z
    .array(z.string())
    .max(5, 'Maximum 5 references allowed')
    .refine(
      (references) => {
        return references.every((ref) => {
          // Skip empty references
          if (!ref || ref.trim() === '') return true;
          
          const wordCount = ref
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0).length;
          
          return wordCount >= 1 && wordCount <= 50;
        });
      },
      { message: 'Each reference must be between 1 and 50 words' }
    ),
})

// Complete abstract validation schema
export const abstractSchema = z.object({
  // Step 1
  mode: z.string().min(1, 'Presentation mode is required'),
  category: z.string().min(1, 'Abstract category is required'),
  topic: z.string().min(1, 'Abstract topic is required'),

  // Step 2
  institutions: z
    .array(institutionSchema)
    .min(1, 'At least one institution is required'),
  authors: z
    .array(authorSchema)
    .min(1, 'At least one author is required')
    .refine(
      (authors) => authors.some((a) => a.is_presenting),
      { message: 'There must be at least one presenting author' }
    )
    .refine(
      (authors) => {
        const correspondingAuthors = authors.filter((a) => a.is_corresponding)
        return (
          correspondingAuthors.length >= 1 && correspondingAuthors.length <= 2
        )
      },
      { message: 'There must be between 1 and 2 corresponding authors' }
    )
    .refine(
      (authors) => {
        // All authors must have positions assigned
        const allAuthorsHavePositions = authors.every(
          (author) => author.position && author.position.trim() !== ''
        )

        if (!allAuthorsHavePositions) {
          return false
        }

        // For multiple authors, validate position uniqueness and main author
        if (authors.length > 1) {
          const positions = authors.map((a) => a.position).filter((p) => p)
          const mainAuthorCount = positions.filter((p) => p === '1').length
          const hasDuplicates = new Set(positions).size !== positions.length
          return mainAuthorCount === 1 && !hasDuplicates
        }

        return true
      },
      {
        message:
          'All authors must have positions assigned. For multiple authors: exactly one main author (position 1) is required and all positions must be unique',
      }
    ),

  // Step 3
  title: z
    .string()
    .min(1, 'Title is required')
    .max(500, 'Title must not exceed 500 characters')
    .regex(
      /^(?!.*[\u4E00-\u9FFF]).*$/s,
      'Title can only contain alphanumerical characters'
    ),
  content: z
    .string()
    .min(1, 'Content is required')
    .regex(
      /^(?!.*[\u4E00-\u9FFF]).*$/s,
      'Content can only contain alphanumerical characters'
    )
    .refine(
      (content) => {
        const wordCount = content
          .trim()
          .split(/\s+/)
          .filter((word) => word.length > 0).length
        return wordCount >= 1 && wordCount <= 300
      },
      { message: 'Content must be between 1 and 300 words' }
    ),
  abstractImages: z
    .array(
      z.object({
        file: z
          .instanceof(File)
          .refine((file) => file.size <= 3.5 * 1024 * 1024, {
            message: 'Each image must not exceed 3.5 MB',
          }),
        preview: z.string(),
        // title: z.string().min(1, 'Image title is required'),
        position: z.number(),
      })
    )
    // .min(1, 'At least one image is required')
    .max(2, 'Maximum 2 images allowed'),
  imagePosition: z.enum(['side_to_side', 'up_and_down'], {
    errorMap: () => ({ message: 'Please select an image display format' }),
  }),
  keywords: z
    .array(z.string())
    .min(1, 'At least one keyword is required')
    .max(5, 'Maximum 5 keywords allowed'),
  references: z
    .array(z.string())
    .max(5, 'Maximum 5 references allowed')
    .refine(
      (references) => {
        return references.every((ref) => {
          // Skip empty references
          if (!ref || ref.trim() === '') return true;
          
          const wordCount = ref
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0).length;
          
          return wordCount >= 1 && wordCount <= 50;
        });
      },
      { message: 'Each reference must be between 1 and 50 words' }
    ),
})

// Validation helper function
export const validateStep = (stepData, schema) => {
  const result = schema.safeParse(stepData)

  if (result.success) {
    return { isValid: true, data: result.data }
  }

  // Convert Zod errors to user-friendly format
  const errors = {}
  result.error.issues.forEach((issue) => {
    const path = issue.path.join('.')
    if (issue.code === 'too_small' && issue.type === 'string') {
      errors[path] = issue.message
    } else if (issue.code === 'too_big' && issue.type === 'string') {
      errors[path] = issue.message
    } else if (issue.code === 'custom') {
      errors[path] = issue.message
    } else {
      errors[path] = issue.message
    }
  })

  return { isValid: false, errors }
}
